import { ComposerIcon, LoadingIcon, PauseIcon, PlayIcon, PublishedIcon, ShareIcon } from '@/components/Icons';
import { usePublishDialog } from '@/hooks/usePublishDialog';
import { AudioDetailDto } from '@/service/interface';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import useGeneratorStore, { GeneratorTypeEnums } from '@/state/generator-store';
import { toast } from 'sonner';
import { useClipboard } from 'use-clipboard-copy';
import { findIndex, isEmpty } from 'lodash-es';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';
import { FilterGroup, SfListItem } from '@/service/type/Filter';
import { useController } from 'react-hook-form';
import { getModelVersion } from '@/pages/Home/Generator/utils';

interface TrackControlProps {
  selectedSongDetail: AudioDetailDto;
}

export const findGroupIndexBySfCode = (groups?: FilterGroup[], sfCode?: string): number => {
  if (!groups || !sfCode) {
    return -1;
  }
  return findIndex(groups, (track: FilterGroup) => {
    return track.sf_list.some((sf: SfListItem) => sf.code === sfCode);
  });
};

export const findSfListIndexByCode = (groups: FilterGroup, sfCode?: string): number => {
  return findIndex(groups.sf_list, (sf: SfListItem) => sf.code === sfCode);
};

export const TrackControl = ({ selectedSongDetail }: TrackControlProps) => {
  const { id, duration, name, is_published: isPublished, created_at: createAt } = selectedSongDetail!;
  const { play, playAudioInfo, isPlaying, isLoading, pause } = useAudioPlayerStore();

  const { control, formValues } = useGeneratorStore((state) => ({
    control: state.control,
    formValues: state.formValues,
  }));

  const {
    field: { onChange: setStyleFilterId, value: styleFilterId },
  } = useController({
    name: 'styleFilterId',
    control,
  });

  const {
    field: { onChange: setStyleFilterIsOpened },
  } = useController({
    name: 'styleFilterIsOpened',
    control,
  });

  const {
    field: { onChange: setStyleFilterWeight, value: filterWeight },
  } = useController({
    name: 'filterWeight',
    control,
    defaultValue: formValues?.filterWeight || undefined,
  });

  const {
    field: { onChange: setModelVersion },
  } = useController({
    name: 'model',
    control,
  });

  const { filterGroupList, setCurrentFilterGroup, setDisabled } = useGeneratorFilterStore();

  const { setGeneratorType, setIsGeneratorDrawerShow, setAudioInfo } = useGeneratorStore((state) => ({
    setIsGeneratorDrawerShow: state.setIsGeneratorDrawerShow,
    setGeneratorType: state.setGeneratorType,
    setAudioInfo: state.setAudioInfo,
  }));

  const isCurrentSong = playAudioInfo?.id === id;
  const renderPlayIcon = () => {
    if (isCurrentSong) {
      if (isLoading) {
        return <LoadingIcon fillColor="#fff" className="p-[10px] box-content  animate-spin" />;
      }
      if (isPlaying) {
        return <PauseIcon fillColor="#fff" className="p-[10px] box-content" onClick={() => pause()} />;
      }
      return <PlayIcon className="p-[10px] box-content" fillColor="#fff" onClick={() => play(playAudioInfo)} />;
    }
    return <PlayIcon fillColor="#fff" className="p-[10px] box-content" onClick={() => play(selectedSongDetail)} />;
  };

  const { onShowPublishDialog, onShowPrivateDialog } = usePublishDialog({ id });

  const clipboard = useClipboard();

  const handleEdit = () => {
    if (isEmpty(selectedSongDetail.stylefilterDetail) || !selectedSongDetail.stylefilterDetail) {
      setDisabled(true);
      setCurrentFilterGroup(null);
      setStyleFilterIsOpened(false);
    } else {
      const groupIndex = findGroupIndexBySfCode(filterGroupList, selectedSongDetail.stylefilterDetail?.code);
      if (groupIndex === -1) {
        toast('style filter not supported');
      } else if (filterGroupList && filterGroupList[groupIndex]) {
        const targetGroup = filterGroupList[groupIndex];
        const sfListIndex = findSfListIndexByCode(targetGroup, selectedSongDetail.stylefilterDetail?.code);
        if (sfListIndex === -1) {
          toast('style filter not supported');
        }
        setCurrentFilterGroup(targetGroup);
        setStyleFilterId(targetGroup.sf_list[sfListIndex].id);
        setStyleFilterWeight(sfListIndex === 0 ? 0 : 1);
        setStyleFilterIsOpened(true);
      }
    }
    pause();

    setModelVersion(getModelVersion(selectedSongDetail) ?? '2.0');
    setAudioInfo(selectedSongDetail);
    setIsGeneratorDrawerShow(true);
    setGeneratorType(GeneratorTypeEnums.EDITOR);
  };

  return (
    <div className=" px-[8px] mt-[16px] ml-[8px] flex gap-[8px]">
      <div className=" cursor-pointer flex justify-center items-center w-[40px] h-[40px] rounded-md bg-jen-interactive-primary hover:bg-orange-500">
        {renderPlayIcon()}
      </div>
      <div className="bg-jen-background-tertiary text-[12px] font-pressura-mono flex-grow rounded-md border border-jen-border grid grid-cols-3 ">
        <div
          className="track_control_box cursor-pointer flex items-center justify-center border-r border-jen-border gap-[4px] hover:text-orange-500"
          onClick={handleEdit}
        >
          <ComposerIcon fillColor="currentColor" /> EDIT
        </div>
        <div
          onClick={() => {
            if (!isPublished) {
              clipboard.copy(`${window.location.origin}/private-s/${id}`);
              toast('Link Copied to Clipboard');
            } else {
              clipboard.copy(`${window.location.origin}/s/${id}`);
              toast('Link Copied to Clipboard');
            }
          }}
          className="cursor-pointer flex w-full items-center justify-center border-r border-jen-border gap-[4px] hover:text-orange-500"
        >
          <ShareIcon fillColor="currentColor" /> SHARE
        </div>
        <div
          onClick={() => {
            isPublished ? onShowPrivateDialog() : onShowPublishDialog({ name, duration, createAt });
          }}
          className="cursor-pointer flex items-center justify-center gap-[4px] hover:text-orange-500"
        >
          <PublishedIcon fillColor="currentColor" /> {isPublished ? 'PUBLISHED' : 'PUBLISH'}
        </div>
      </div>
    </div>
  );
};
